import React, { useState } from "react";
import ReferralAnimation from "../animations/ReferralAnimation";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL;

export default function ReferralSystemPage() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const getReferralCode = async () => {
    if (!phoneNumber.trim()) {
      setError("Please enter your WhatsApp number.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/get-referral-code/${encodeURIComponent(phoneNumber)}`
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not fetch referral code.");
      }

      setResult(data.data);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const sendCodeToEmail = async () => {
    if (!phoneNumber.trim()) {
      setError("Please enter your WhatsApp number first.");
      return;
    }

    setEmailLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/send-referral-code-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not send email.");
      }

      alert("Referral code email sent successfully.");
    } catch (err) {
      setError(err.message || "Could not send referral code email.");
    } finally {
      setEmailLoading(false);
    }
  };

  const referralCode = result?.referral_code || "";

  const shareText = referralCode
    ? `Join Zaviramed and chat with a doctor on WhatsApp. Use my referral code: ${referralCode}`
    : "Join Zaviramed and chat with a doctor on WhatsApp.";

  const whatsappShareLink = `https://wa.me/?text=${encodeURIComponent(
    shareText
  )}`;

  return (
    <main className="ref-wrap">
      <div className="ref-bg ref-bg-a" />
      <div className="ref-bg ref-bg-b" />
      <div className="ref-bg ref-bg-c" />
      <div className="ref-grid" />

      <section className="ref-hero">
        <div className="ref-copy">
          <div className="ref-pill">
            <span />
            Zaviramed Referral Program
          </div>

          <h1>
            Refer 4 patients.
            <span>Get 1 consultation free.</span>
          </h1>

          <p className="ref-sub">
            Get your referral code, share it with friends and family on
            WhatsApp, and when they text Zaviramed and enter your code, your
            free consultation balance increases.
          </p>

          <div className="ref-reward-strip">
            <div>
              <strong>1 referral</strong>
              <span>+0.25 free consultation</span>
            </div>

            <div>
              <strong>4 referrals</strong>
              <span>1 full consultation free</span>
            </div>
          </div>

          <div className="ref-card">
            <div className="ref-card-head">
              <div>
                <small>Get your referral code</small>
                <strong>Enter your WhatsApp number</strong>
              </div>

              <div className="ref-badge">4 referrals = 1 free consult</div>
            </div>

            <div className="ref-input-row">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="08069804062, 2348069804062 or +2348069804062"
                onKeyDown={(e) => {
                  if (e.key === "Enter") getReferralCode();
                }}
              />

              <button onClick={getReferralCode} disabled={loading}>
                {loading ? "Checking..." : "Get Code"}
              </button>
            </div>

            <div className="ref-format-note">
              Works with 080 format, 234 format, +234 format, and WhatsApp
              format.
            </div>

            {error ? <div className="ref-error">{error}</div> : null}

            {result ? (
              <div className="ref-result">
                <div className="ref-code-box">
                  <small>Your Referral Code</small>
                  <strong>{result.referral_code}</strong>
                  <button
                    type="button"
                    id='copy-btn'
                    onClick={(e) => {
                      navigator.clipboard.writeText(result.referral_code);
                      document.getElementById('copy-btn').innerHTML = "Code Copied!";
                      setTimeout(()=> {
                        document.getElementById('copy-btn').innerHTML = "Copy Code  ";
                      },2000)
                    }}
                  >
                    Copy Code
                  </button>
                </div>

                
              </div>
            ) : null}
          </div>

          <div className="ref-steps">
            <div>
              <span>1</span>
              <p>You get your personal referral code.</p>
            </div>

            <div>
              <span>2</span>
              <p>You share it with friends and family on WhatsApp.</p>
            </div>

            <div>
              <span>3</span>
              <p>They text Zaviramed and are asked for a referral code.</p>
            </div>

            <div>
              <span>4</span>
              <p>They enter your code and your free consultations go up.</p>
            </div>
          </div>
        </div>

        <ReferralAnimation />
      </section>

      <style>{styles}</style>
    </main>
  );
}

const styles = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900;1000&family=Sora:wght@400;500;600;700;800&display=swap');

:root {
  --ref-green: #16a34a;
  --ref-green-2: #22c55e;
  --ref-teal: #0f766e;
  --ref-dark: #052e2b;
  --ref-ink: #0f172a;
  --ref-muted: #64748b;
  --ref-soft: #f0fdf4;
  --ref-border: rgba(15, 23, 42, 0.08);
}

* {
  box-sizing: border-box;
}

.ref-wrap,
.ref-wrap * {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.ref-wrap {
  position: relative;
  overflow: hidden;
  min-height: 100svh;
  padding: clamp(18px, 3vw, 46px);
  background:
    radial-gradient(circle at 10% 8%, rgba(34,197,94,.17), transparent 28%),
    radial-gradient(circle at 86% 10%, rgba(14,165,233,.14), transparent 28%),
    radial-gradient(circle at 78% 84%, rgba(20,184,166,.12), transparent 24%),
    linear-gradient(135deg, #f8fffb 0%, #f4fff8 45%, #f8fafc 100%);
  isolation: isolate;
}

.ref-bg {
  position: absolute;
  border-radius: 999px;
  filter: blur(20px);
  z-index: -2;
  animation: refFloat 11s ease-in-out infinite;
}

.ref-bg-a {
  width: 260px;
  height: 260px;
  left: -70px;
  top: 120px;
  background: rgba(187, 247, 208, .75);
}

.ref-bg-b {
  width: 340px;
  height: 340px;
  right: -110px;
  top: 70px;
  background: rgba(165, 243, 252, .75);
  animation-delay: -4s;
}

.ref-bg-c {
  width: 260px;
  height: 260px;
  right: 25%;
  bottom: -80px;
  background: rgba(191, 219, 254, .65);
  animation-delay: -7s;
}

.ref-grid {
  position: absolute;
  inset: 0;
  z-index: -3;
  background-image: radial-gradient(rgba(15,118,110,.12) 1px, transparent 1px);
  background-size: 24px 24px;
  opacity: .7;
  mask-image: linear-gradient(to bottom, transparent, black 16%, black 84%, transparent);
}

.ref-hero {
  width: min(1180px, 100%);
  min-height: calc(100svh - clamp(36px, 6vw, 92px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr .9fr;
  gap: clamp(28px, 4vw, 60px);
  align-items: center;
}

.ref-copy {
  position: relative;
  z-index: 2;
}

.ref-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  border-radius: 999px;
  background: rgba(255,255,255,.82);
  border: 1px solid rgba(15,118,110,.14);
  color: var(--ref-teal);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .14em;
  box-shadow: 0 18px 40px rgba(15,23,42,.05);
}

.ref-pill span {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--ref-green-2);
  box-shadow: 0 0 0 6px rgba(34,197,94,.14);
}

.ref-copy h1 {
  max-width: 680px;
  margin: 20px 0 0;
  color: #071b18;
  font-family: 'Sora', sans-serif;
  font-size: clamp(38px, 5vw, 68px);
  line-height: .94;
  letter-spacing: -.07em;
  font-weight: 800;
}

.ref-copy h1 span {
  display: block;
  margin-top: 12px;
  background: linear-gradient(135deg, #0f766e, #16a34a, #0ea5e9);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.ref-sub {
  max-width: 570px;
  margin: 18px 0 0;
  color: #475569;
  font-size: clamp(16px, 1.45vw, 19px);
  line-height: 1.75;
  font-weight: 500;
}

.ref-reward-strip {
  max-width: 610px;
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ref-reward-strip div {
  padding: 16px;
  border-radius: 24px;
  background: rgba(255,255,255,.78);
  border: 1px solid var(--ref-border);
  box-shadow: 0 16px 40px rgba(15,23,42,.05);
}

.ref-reward-strip strong {
  display: block;
  color: var(--ref-ink);
  font-family: 'Sora', sans-serif;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -.03em;
}

.ref-reward-strip span {
  display: block;
  margin-top: 6px;
  color: var(--ref-muted);
  font-size: 13px;
  font-weight: 800;
}

.ref-card {
  max-width: 610px;
  margin-top: 18px;
  padding: 18px;
  border-radius: 30px;
  background: rgba(255,255,255,.9);
  border: 1px solid var(--ref-border);
  box-shadow: 0 24px 70px rgba(15,23,42,.09);
  backdrop-filter: blur(12px);
}

.ref-card-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.ref-card-head small {
  display: block;
  color: var(--ref-muted);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .12em;
}

.ref-card-head strong {
  display: block;
  margin-top: 4px;
  color: var(--ref-ink);
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -.03em;
}

.ref-badge {
  flex: 0 0 auto;
  padding: 9px 12px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 900;
}

.ref-input-row {
  display: flex;
  gap: 10px;
  padding: 8px;
  border-radius: 22px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.ref-input-row input {
  flex: 1;
  min-width: 0;
  height: 48px;
  border: 0;
  outline: 0;
  background: transparent;
  padding: 0 12px;
  color: var(--ref-ink);
  font-size: 15px;
  font-weight: 700;
}

.ref-input-row button,
.ref-actions button,
.ref-actions a,
.ref-code-box button {
  border: 0;
  outline: 0;
  cursor: pointer;
  min-height: 48px;
  border-radius: 16px;
  padding: 0 18px;
  background: var(--ref-green);
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-shadow: 0 12px 28px rgba(22,163,74,.22);
}

.ref-input-row button:disabled,
.ref-actions button:disabled {
  opacity: .65;
  cursor: not-allowed;
}

.ref-format-note {
  margin-top: 10px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 700;
}

.ref-error {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 13px;
  font-weight: 800;
}

.ref-result {
  margin-top: 16px;
  display: grid;
  gap: 14px;
}

.ref-code-box {
  padding: 18px;
  border-radius: 22px;
  background: #f0fdf4;
  border: 1px dashed #16a34a;
  text-align: center;
}

.ref-code-box small {
  display: block;
  color: #15803d;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .13em;
}

.ref-code-box strong {
  display: block;
  margin-top: 8px;
  color: #064e3b;
  font-family: 'Sora', sans-serif;
  font-size: 34px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -.03em;
}

.ref-code-box button {
  min-height: 38px;
  margin-top: 14px;
  background: #ffffff;
  color: #0f766e;
  border: 1px solid #99f6e4;
  box-shadow: none;
}

.ref-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.ref-stats div {
  padding: 14px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.ref-stats small {
  display: block;
  color: var(--ref-muted);
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .1em;
}

.ref-stats strong {
  display: block;
  margin-top: 5px;
  color: var(--ref-ink);
  font-size: 20px;
  font-weight: 900;
}

.ref-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.ref-actions button {
  background: #ffffff;
  color: var(--ref-teal);
  border: 1px solid #99f6e4;
  box-shadow: none;
}

.ref-steps {
  max-width: 610px;
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ref-steps div {
  display: flex;
  gap: 12px;
  padding: 14px;
  border-radius: 22px;
  background: rgba(255,255,255,.74);
  border: 1px solid var(--ref-border);
}

.ref-steps span {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: #dcfce7;
  color: #166534;
  font-size: 13px;
  font-weight: 900;
}

.ref-steps p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 700;
}

@keyframes refFloat {
  0%, 100% { transform: translate3d(0,0,0) scale(1); }
  50% { transform: translate3d(16px,-20px,0) scale(1.06); }
}

@media (max-width: 980px) {
  .ref-hero {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 26px;
  }

  .ref-pill,
  .ref-copy h1,
  .ref-sub,
  .ref-reward-strip,
  .ref-card,
  .ref-steps {
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 560px) {
  .ref-wrap {
    padding: 16px;
  }

  .ref-copy h1 {
    font-size: 38px;
  }

  .ref-sub {
    font-size: 15.5px;
  }

  .ref-reward-strip {
    grid-template-columns: 1fr;
  }

  .ref-card {
    padding: 14px;
    border-radius: 26px;
  }

  .ref-card-head {
    flex-direction: column;
  }

  .ref-input-row {
    flex-direction: column;
  }

  .ref-input-row button {
    width: 100%;
  }

  .ref-stats {
    grid-template-columns: 1fr;
  }

  .ref-actions {
    flex-direction: column;
  }

  .ref-actions a,
  .ref-actions button {
    width: 100%;
  }

  .ref-steps {
    grid-template-columns: 1fr;
  }
}
`;